import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { API_TIMEOUT_MS } from '@/utils';
import { tokenStorage } from './tokenStorage';

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: API_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
});

// ---- Request Interceptor: attach access token ----
api.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---- Refresh Token Queue ----
let isRefreshing = false;
let pendingQueue: Array<(token: string | null) => void> = [];

function flushQueue(token: string | null): void {
  pendingQueue.forEach((cb) => cb(token));
  pendingQueue = [];
}

type RefreshFn = () => Promise<string | null>;
let refreshFn: RefreshFn | null = null;

export function registerRefreshHandler(fn: RefreshFn): void {
  refreshFn = fn;
}

type UnauthorizedFn = () => void;
let onUnauthorized: UnauthorizedFn | null = null;
export function registerUnauthorizedHandler(fn: UnauthorizedFn): void {
  onUnauthorized = fn;
}

// ---- Response Interceptor: 401 -> refresh + replay ----
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;
    if (!original || !error.response) return Promise.reject(error);

    const status = error.response.status;
    const isAuthEndpoint =
      original.url?.includes('/auth/login') || original.url?.includes('/auth/refresh');

    if (status === 401 && !original._retry && !isAuthEndpoint) {
      original._retry = true;

      if (!refreshFn) {
        onUnauthorized?.();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push((token) => {
            if (token) {
              if (original.headers) original.headers.Authorization = `Bearer ${token}`;
              resolve(api(original as AxiosRequestConfig));
            } else {
              reject(error);
            }
          });
        });
      }

      isRefreshing = true;
      try {
        const newToken = await refreshFn();
        flushQueue(newToken);
        if (newToken) {
          if (original.headers) original.headers.Authorization = `Bearer ${newToken}`;
          return api(original as AxiosRequestConfig);
        }
        onUnauthorized?.();
        return Promise.reject(error);
      } catch (refreshError) {
        flushQueue(null);
        onUnauthorized?.();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
